import { useEffect, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import {
  modalOriginPositionAtom,
  iframeFocusModeAtom,
} from "@zapier/canvas-state";
import { FOCUS_MODE_ANIMATION } from "@zapier/canvas-constants";
import { preloadService } from "../services/PreloadService";

/**
 * Custom hook for managing preloaded iframes
 *
 * Handles the complete lifecycle of taking a preloaded iframe from the PreloadService,
 * managing its readiness state, and cleaning up when the component unmounts.
 *
 * @param url - The URL to check for preloaded iframe
 * @param sideBarWidth - Current sidebar width for iframe positioning
 * @param isClosing - Whether the modal is closing (for animation)
 * @returns Object containing iframe state and references
 */
export function usePreloadedIframe(
  url: string | undefined,
  sideBarWidth: number,
  isClosing?: boolean,
) {
  const [preloadedIframe, setPreloadedIframe] =
    useState<HTMLIFrameElement | null>(null);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const modalOriginPosition = useAtomValue(modalOriginPositionAtom);
  const setActiveFocusIframe = useSetAtom(iframeFocusModeAtom);

  // Check for preloaded iframe on mount/URL change
  useEffect(() => {
    // Reset readiness state when URL changes
    setIsIframeReady(false);

    if (url) {
      const result = preloadService.take(
        url,
        sideBarWidth,
        modalOriginPosition,
      );

      if (result) {
        // Iframe stays in document.body, just repositioned by service
        setPreloadedIframe(result.iframe);
        setIsPreloaded(true);
        setIsIframeReady(result.isReady);

        // If not ready yet, poll for readiness
        if (!result.isReady) {
          const pollForReady = () => {
            // Check if iframe has finished loading
            if (result.iframe.dataset.preloadStatus === "loaded") {
              setIsIframeReady(true);
            } else {
              // Keep polling every 100ms
              setTimeout(pollForReady, 100);
            }
          };

          pollForReady();

          // Fallback timeout in case iframe never loads (safety net)
          const fallbackTimeout = setTimeout(() => {
            setIsIframeReady(true);
          }, 5000);

          // Cleanup function to clear the timeout
          return () => {
            clearTimeout(fallbackTimeout);
          };
        }
      } else {
        setPreloadedIframe(null);
        setIsPreloaded(false);
        setIsIframeReady(false);
      }
    } else {
      // No URL provided, reset all state
      setPreloadedIframe(null);
      setIsPreloaded(false);
      setIsIframeReady(false);
    }
  }, [url, sideBarWidth, modalOriginPosition]);

  // Set iframe atom when visible, clear when hidden
  useEffect(() => {
    if (preloadedIframe && isIframeReady && !isClosing) {
      // Iframe is loaded and visible
      setActiveFocusIframe(preloadedIframe);
    } else {
      // Iframe is hidden or not ready
      setActiveFocusIframe(null);
    }
  }, [preloadedIframe, isIframeReady, isClosing, setActiveFocusIframe]);

  // Simplified closing animation with timing constants
  useEffect(() => {
    if (isClosing && preloadedIframe) {
      // Start the closing animation using timing constants
      preloadedIframe.style.transition = `transform ${FOCUS_MODE_ANIMATION.DURATION}ms ${FOCUS_MODE_ANIMATION.EASING}, opacity ${FOCUS_MODE_ANIMATION.DURATION}ms ${FOCUS_MODE_ANIMATION.EASING}`;
      preloadedIframe.style.transform = "scale3d(0, 0, 1)";
      preloadedIframe.style.opacity = "0";
    }
  }, [isClosing, preloadedIframe]);

  // Cleanup: Hide iframe when modal closes
  useEffect(() => {
    return () => {
      if (preloadedIframe) {
        // Hide the iframe when modal closes
        preloadedIframe.style.opacity = "0";
        preloadedIframe.style.zIndex = "-1";
        preloadedIframe.style.pointerEvents = "none";
        // Reset scale for next use
        preloadedIframe.style.transform = "scale(0)";
        preloadedIframe.style.transition = "";
      }
    };
  }, [preloadedIframe]);

  // Show loader if no preloaded iframe OR if iframe exists but not ready yet
  const showLoader = !isPreloaded || !isIframeReady;

  return {
    isPreloaded,
    showLoader,
  };
}
